import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, X, Info } from "lucide-react";

/**
 * Themed dialog primitives — replace browser-default window.confirm() and
 * window.prompt() across the app. Mounted via portal at #root so they sit
 * above all other modals (z-index 10050).
 *
 * Public API (Promise-based — easy to drop into any handler):
 *   import { themedConfirm, themedPrompt, DialogHost } from "./ConfirmDialog";
 *
 *   // 1. Mount <DialogHost /> once near the top of App.
 *   // 2. Anywhere:
 *      const ok = await themedConfirm({ title, message, tone: "danger" });
 *      if (!ok) return;
 *      const reason = await themedPrompt({ title, message, placeholder, required: true });
 *      if (reason === null) return; // cancelled
 */

// ---------- Tiny pub/sub bus ----------
const subscribers = new Set();
let nextId = 1;
const push = (dialog) => {
    const id = nextId++;
    subscribers.forEach((fn) => fn({ ...dialog, id }));
    return id;
};

export function themedConfirm(opts = {}) {
    return new Promise((resolve) => {
        push({
            kind: "confirm",
            title: opts.title || "Are you sure?",
            message: opts.message || "",
            confirmLabel: opts.confirmLabel || "Yes",
            cancelLabel: opts.cancelLabel || "Cancel",
            tone: opts.tone || "primary", // primary | danger | warn
            resolve,
        });
    });
}

export function themedPrompt(opts = {}) {
    return new Promise((resolve) => {
        push({
            kind: "prompt",
            title: opts.title || "Please enter a value",
            message: opts.message || "",
            placeholder: opts.placeholder || "",
            defaultValue: opts.defaultValue || "",
            confirmLabel: opts.confirmLabel || "Submit",
            cancelLabel: opts.cancelLabel || "Cancel",
            required: !!opts.required,
            minLength: opts.minLength || 0,
            multiline: !!opts.multiline,
            tone: opts.tone || "primary",
            resolve,
        });
    });
}

// ---------- Host (mount once) ----------
export function DialogHost() {
    const [stack, setStack] = useState([]);

    useEffect(() => {
        const handler = (d) => setStack((s) => [...s, d]);
        subscribers.add(handler);
        return () => { subscribers.delete(handler); };
    }, []);

    const close = useCallback((id, val) => {
        setStack((s) => {
            const d = s.find((x) => x.id === id);
            if (d) d.resolve(val);
            return s.filter((x) => x.id !== id);
        });
    }, []);

    if (stack.length === 0) return null;
    return createPortal(
        <>
            {stack.map((d) => <Dialog key={d.id} dialog={d} onResolve={(v) => close(d.id, v)} />)}
        </>,
        document.body
    );
}

function Dialog({ dialog, onResolve }) {
    const [value, setValue] = useState(dialog.defaultValue || "");
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => inputRef.current?.focus(), 80);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") { e.stopPropagation(); onResolve(dialog.kind === "prompt" ? null : false); }
            else if (e.key === "Enter" && dialog.kind === "confirm") { e.stopPropagation(); onResolve(true); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [dialog.kind, onResolve]);

    const handleConfirm = () => {
        if (dialog.kind === "prompt") {
            const trimmed = (value || "").trim();
            if (dialog.required && !trimmed) { setError("This field is required."); return; }
            if (dialog.minLength && trimmed.length < dialog.minLength) {
                setError(`Please write at least ${dialog.minLength} characters.`); return;
            }
            onResolve(trimmed);
        } else {
            onResolve(true);
        }
    };

    const handleCancel = () => onResolve(dialog.kind === "prompt" ? null : false);

    const Icon = dialog.tone === "danger" ? AlertTriangle : dialog.tone === "warn" ? AlertTriangle : dialog.kind === "prompt" ? Info : Check;
    const iconBg = dialog.tone === "danger" ? "bg-red-50 text-red-600" : dialog.tone === "warn" ? "bg-amber-50 text-amber-600" : "bg-ivory-soft text-rosegold";
    const btnClass = dialog.tone === "danger"
        ? "bg-red-600 hover:bg-red-700 text-white"
        : dialog.tone === "warn"
            ? "bg-amber-500 hover:bg-amber-600 text-white"
            : "bg-ink hover:bg-rosegold text-white";

    return (
        <div className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-stone-900/50 backdrop-blur-sm" data-testid="themed-dialog" onClick={(e) => e.stopPropagation()}>
            <div
                className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="p-5 sm:p-6">
                    <div className="flex items-start gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                            <Icon size={18} />
                        </div>
                        <div className="flex-1 min-w-0 pt-1">
                            <h2 className="font-heading text-lg font-semibold text-stone-900" data-testid="dialog-title">{dialog.title}</h2>
                            {dialog.message && <p className="text-sm text-stone-600 mt-1 whitespace-pre-line" data-testid="dialog-message">{dialog.message}</p>}
                        </div>
                        <button onClick={handleCancel} className="p-1.5 -mt-1 -mr-1 rounded-lg hover:bg-stone-100 text-stone-500" data-testid="dialog-close" aria-label="Close">
                            <X size={16} />
                        </button>
                    </div>

                    {dialog.kind === "prompt" && (
                        <div className="mt-4">
                            {dialog.multiline ? (
                                <textarea
                                    ref={inputRef}
                                    value={value}
                                    onChange={(e) => { setValue(e.target.value); setError(""); }}
                                    placeholder={dialog.placeholder}
                                    rows={4}
                                    data-testid="dialog-input"
                                    className="w-full px-3 py-2.5 rounded-md border border-stone-300 bg-white text-sm focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20 resize-none"
                                />
                            ) : (
                                <input
                                    ref={inputRef}
                                    value={value}
                                    onChange={(e) => { setValue(e.target.value); setError(""); }}
                                    placeholder={dialog.placeholder}
                                    data-testid="dialog-input"
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleConfirm(); } }}
                                    className="w-full h-11 px-3 rounded-md border border-stone-300 bg-white text-sm focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20"
                                />
                            )}
                            {error && <p className="text-[11px] text-red-600 mt-1.5" data-testid="dialog-error">{error}</p>}
                        </div>
                    )}
                </div>

                <div className="px-5 sm:px-6 py-3 bg-stone-50 border-t border-stone-200 flex gap-2 justify-end">
                    <button
                        onClick={handleCancel}
                        data-testid="dialog-cancel"
                        className="px-4 h-10 rounded-md text-sm font-semibold text-stone-700 hover:bg-stone-200 transition"
                    >
                        {dialog.cancelLabel}
                    </button>
                    <button
                        onClick={handleConfirm}
                        data-testid="dialog-confirm"
                        className={`px-4 h-10 rounded-md text-sm font-semibold transition ${btnClass}`}
                    >
                        {dialog.confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DialogHost;
